"use client";

import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { WalletConnectModalContext } from "../providers/wallet-connect-modal-provider";
import { useMediaQuery } from "../../../hooks/use-media-query";
import { useWalletDetect } from "../hooks/use-wallet-detect";
import { WalletConnectModalTrigger } from "./WalletConnectModalTrigger";
import { WalletConnectModalContent } from "./WalletConnectModalContent";
import type { WalletConnectModalProps } from "../types";
import type { SocialId, WalletId } from "../constants";
import { DEFAULT_TERMS_URL, DEFAULT_PRIVACY_URL } from "../../../lib/constants";
import "../styles/component.css";
import "../styles/keyframes.css";

const EMPTY_SOCIAL_PROVIDERS = {} as const;

function WalletConnectModalRoot({
  wallets,
  socialProviders = EMPTY_SOCIAL_PROVIDERS,
  termsUrl,
  privacyUrl,
  theme = "dark",
  mobileBreakpoint = 768,
  drawerDirection = "bottom",
  dialogWidth,
  drawerMaxWidth,
  drawerMinWidth,
  style,
  open: openProp,
  onOpenChange,
  children,
}: WalletConnectModalProps) {
  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleOpenChange = React.useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  const close = React.useCallback(
    () => handleOpenChange(false),
    [handleOpenChange],
  );
  const { isDetected } = useWalletDetect();

  const activeWalletIds = React.useMemo(
    () => Object.keys(wallets) as WalletId[],
    [wallets],
  );

  const activeSocialIds = React.useMemo(
    () => Object.keys(socialProviders) as SocialId[],
    [socialProviders],
  );

  const mergedStyle = React.useMemo(() => {
    const sizeVars: Record<string, string> = {};
    if (dialogWidth) sizeVars["--wcm-dialog-width"] = dialogWidth;
    if (drawerMaxWidth) sizeVars["--wcm-drawer-max-width"] = drawerMaxWidth;
    if (drawerMinWidth) sizeVars["--wcm-drawer-min-width"] = drawerMinWidth;
    return { ...sizeVars, ...style };
  }, [dialogWidth, drawerMaxWidth, drawerMinWidth, style]);

  const contextValue = React.useMemo(
    () => ({
      theme,
      isMobile,
      open,
      setOpen: handleOpenChange,
      close,
      wallets,
      activeWalletIds,
      socialProviders,
      activeSocialIds,
      // 미설정 시 dapp-ui 공통 문서로 폴백해 약관/정책 링크가 항상 노출되게 한다.
      termsUrl: termsUrl ?? DEFAULT_TERMS_URL,
      privacyUrl: privacyUrl ?? DEFAULT_PRIVACY_URL,
      isDetected,
      portalStyle: mergedStyle,
    }),
    [
      theme,
      isMobile,
      open,
      handleOpenChange,
      close,
      wallets,
      activeWalletIds,
      socialProviders,
      activeSocialIds,
      termsUrl,
      privacyUrl,
      isDetected,
      mergedStyle,
    ],
  );

  const wrapper = (
    <div data-theme={theme} style={mergedStyle}>
      {children}
    </div>
  );

  if (isMobile) {
    return (
      <WalletConnectModalContext.Provider value={contextValue}>
        <Drawer.Root
          key="drawer"
          direction={drawerDirection}
          open={open}
          onOpenChange={handleOpenChange}
          noBodyStyles
        >
          {wrapper}
        </Drawer.Root>
      </WalletConnectModalContext.Provider>
    );
  }

  return (
    <WalletConnectModalContext.Provider value={contextValue}>
      <Dialog.Root open={open} onOpenChange={handleOpenChange}>
        {wrapper}
      </Dialog.Root>
    </WalletConnectModalContext.Provider>
  );
}

export const WalletConnectModal = Object.assign(WalletConnectModalRoot, {
  Trigger: WalletConnectModalTrigger,
  Content: WalletConnectModalContent,
});
